/**
 * Color Preset System
 *
 * Allows easy switching between pre-defined color schemes.
 * Each preset defines primary, secondary, tertiary, and accent colors.
 *
 * Usage:
 *   import { applyColorPreset, getAvailablePresets, getCurrentPreset } from './utils/colorPresets.js'
 *
 *   // Apply a preset
 *   applyColorPreset('ocean')
 *
 *   // Get all available presets
 *   const presets = getAvailablePresets()
 *
 *   // Get current preset
 *   const current = getCurrentPreset()
 */

export const COLOR_PRESETS = {
  default: {
    name: 'Default',
    description: 'Purple primary with blue secondary - classic and professional',
    colors: {
      // Primary - Purple
      primary: {
        50: '#faf5ff',
        100: '#f3e8ff',
        200: '#e9d5ff',
        300: '#d8b4fe',
        400: '#c084fc',
        500: '#a855f7',
        600: '#9333ea',
        700: '#7e22ce',
        800: '#6b21a8',
        900: '#581c87',
        950: '#3b0764',
      },
      // Secondary - Blue
      secondary: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6',
        600: '#2563eb',
        700: '#1d4ed8',
        800: '#1e40af',
        900: '#1e3a8a',
        950: '#172554',
      },
      // Tertiary - Teal
      tertiary: {
        50: '#f0fdfa',
        100: '#ccfbf1',
        200: '#99f6e4',
        300: '#5eead4',
        400: '#2dd4bf',
        500: '#14b8a6',
        600: '#0d9488',
        700: '#0f766e',
        800: '#115e59',
        900: '#134e4a',
        950: '#042f2e',
      },
      // Accent - Orange
      accent: {
        50: '#fff7ed',
        100: '#ffedd5',
        200: '#fed7aa',
        300: '#fdba74',
        400: '#fb923c',
        500: '#f97316',
        600: '#ea580c',
        700: '#c2410c',
        800: '#9a3412',
        900: '#7c2d12',
        950: '#431407',
      },
    },
  },

  ocean: {
    name: 'Ocean',
    description: 'Cool blues and teals - calm and trustworthy',
    colors: {
      // Primary - Blue
      primary: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6',
        600: '#2563eb',
        700: '#1d4ed8',
        800: '#1e40af',
        900: '#1e3a8a',
        950: '#172554',
      },
      // Secondary - Teal
      secondary: {
        50: '#f0fdfa',
        100: '#ccfbf1',
        200: '#99f6e4',
        300: '#5eead4',
        400: '#2dd4bf',
        500: '#14b8a6',
        600: '#0d9488',
        700: '#0f766e',
        800: '#115e59',
        900: '#134e4a',
        950: '#042f2e',
      },
      // Tertiary - Cyan
      tertiary: {
        50: '#ecfeff',
        100: '#cffafe',
        200: '#a5f3fc',
        300: '#67e8f9',
        400: '#22d3ee',
        500: '#06b6d4',
        600: '#0891b2',
        700: '#0e7490',
        800: '#155e75',
        900: '#164e63',
        950: '#083344',
      },
      // Accent - Coral/Orange
      accent: {
        50: '#fff1f2',
        100: '#ffe4e6',
        200: '#fecdd3',
        300: '#fda4af',
        400: '#fb7185',
        500: '#f43f5e',
        600: '#e11d48',
        700: '#be123c',
        800: '#9f1239',
        900: '#881337',
        950: '#4c0519',
      },
    },
  },

  forest: {
    name: 'Forest',
    description: 'Natural greens and earth tones - organic and eco-friendly',
    colors: {
      // Primary - Green
      primary: {
        50: '#f0fdf4',
        100: '#dcfce7',
        200: '#bbf7d0',
        300: '#86efac',
        400: '#4ade80',
        500: '#22c55e',
        600: '#16a34a',
        700: '#15803d',
        800: '#166534',
        900: '#14532d',
        950: '#052e16',
      },
      // Secondary - Emerald
      secondary: {
        50: '#ecfdf5',
        100: '#d1fae5',
        200: '#a7f3d0',
        300: '#6ee7b7',
        400: '#34d399',
        500: '#10b981',
        600: '#059669',
        700: '#047857',
        800: '#065f46',
        900: '#064e3b',
        950: '#022c22',
      },
      // Tertiary - Teal
      tertiary: {
        50: '#f0fdfa',
        100: '#ccfbf1',
        200: '#99f6e4',
        300: '#5eead4',
        400: '#2dd4bf',
        500: '#14b8a6',
        600: '#0d9488',
        700: '#0f766e',
        800: '#115e59',
        900: '#134e4a',
        950: '#042f2e',
      },
      // Accent - Amber
      accent: {
        50: '#fffbeb',
        100: '#fef3c7',
        200: '#fde68a',
        300: '#fcd34d',
        400: '#fbbf24',
        500: '#f59e0b',
        600: '#d97706',
        700: '#b45309',
        800: '#92400e',
        900: '#78350f',
        950: '#451a03',
      },
    },
  },

  sunset: {
    name: 'Sunset',
    description: 'Warm oranges and reds - energetic and bold',
    colors: {
      // Primary - Orange
      primary: {
        50: '#fff7ed',
        100: '#ffedd5',
        200: '#fed7aa',
        300: '#fdba74',
        400: '#fb923c',
        500: '#f97316',
        600: '#ea580c',
        700: '#c2410c',
        800: '#9a3412',
        900: '#7c2d12',
        950: '#431407',
      },
      // Secondary - Red
      secondary: {
        50: '#fef2f2',
        100: '#fee2e2',
        200: '#fecaca',
        300: '#fca5a5',
        400: '#f87171',
        500: '#ef4444',
        600: '#dc2626',
        700: '#b91c1c',
        800: '#991b1b',
        900: '#7f1d1d',
        950: '#450a0a',
      },
      // Tertiary - Pink
      tertiary: {
        50: '#fdf2f8',
        100: '#fce7f3',
        200: '#fbcfe8',
        300: '#f9a8d4',
        400: '#f472b6',
        500: '#ec4899',
        600: '#db2777',
        700: '#be185d',
        800: '#9d174d',
        900: '#831843',
        950: '#500724',
      },
      // Accent - Yellow
      accent: {
        50: '#fefce8',
        100: '#fef9c3',
        200: '#fef08a',
        300: '#fde047',
        400: '#facc15',
        500: '#eab308',
        600: '#ca8a04',
        700: '#a16207',
        800: '#854d0e',
        900: '#713f12',
        950: '#422006',
      },
    },
  },
};

const STORAGE_KEY = 'color-preset';

/**
 * Apply a color preset to the document
 * @param {string} presetName - Name of the preset to apply ('default', 'ocean', 'forest', 'sunset')
 */
export function applyColorPreset(presetName = 'default') {
  const preset = COLOR_PRESETS[presetName];
  if (!preset) {
    console.error(`Color preset "${presetName}" not found. Using default.`);
    presetName = 'default';
  }

  const { colors } = COLOR_PRESETS[presetName];
  const root = document.documentElement;

  // Apply primary colors
  Object.entries(colors.primary).forEach(([shade, value]) => {
    root.style.setProperty(`--color-primary-${shade}`, value);
  });

  // Apply secondary colors
  Object.entries(colors.secondary).forEach(([shade, value]) => {
    root.style.setProperty(`--color-secondary-${shade}`, value);
  });

  // Apply tertiary colors
  Object.entries(colors.tertiary).forEach(([shade, value]) => {
    root.style.setProperty(`--color-tertiary-${shade}`, value);
  });

  // Apply accent colors
  Object.entries(colors.accent).forEach(([shade, value]) => {
    root.style.setProperty(`--color-accent-${shade}`, value);
  });

  // Save preference
  localStorage.setItem(STORAGE_KEY, presetName);

  // Dispatch custom event for other components to listen to
  window.dispatchEvent(new CustomEvent('color-preset-changed', {
    detail: { preset: presetName }
  }));

  console.log(`Applied color preset: ${COLOR_PRESETS[presetName].name}`);
}

/**
 * Get the currently active color preset
 * @returns {string} The name of the current preset
 */
export function getCurrentPreset() {
  return localStorage.getItem(STORAGE_KEY) || 'default';
}

/**
 * Get all available color presets
 * @returns {Array} Array of preset objects with name and description
 */
export function getAvailablePresets() {
  return Object.entries(COLOR_PRESETS).map(([key, preset]) => ({
    id: key,
    name: preset.name,
    description: preset.description,
  }));
}

/**
 * Initialize color preset system
 * Should be called on page load to restore saved preference
 */
export function initColorPresets() {
  const savedPreset = getCurrentPreset();
  applyColorPreset(savedPreset);
}

// Auto-initialize if script is loaded
if (typeof window !== 'undefined') {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initColorPresets);
  } else {
    initColorPresets();
  }
}
